import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  LogOut,
  Check,
  X,
  Eye,
  Clock,
  Undo2,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/simple-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { dataStore, ROUTES } from "@/lib/store";
import { supabaseStore } from "@/lib/supabaseStore";
import { useSupabase } from "@/hooks/useSupabase";
import { Athlete, Judge, Attempt } from "@shared/types";

export default function JudgeArea() {
  const { store, isConnected } = useSupabase();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentJudge, setCurrentJudge] = useState<Judge | null>(null);
  const [password, setPassword] = useState("");
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<"11:00" | "14:30" | null>(null);
  const [selectedAthlete, setSelectedAthlete] = useState<Athlete | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [athleteSearch, setAthleteSearch] = useState("");
  const [filteredAthletes, setFilteredAthletes] = useState<Athlete[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [athleteScore, setAthleteScore] = useState<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isUpdatingData, setIsUpdatingData] = useState(false);
  const [isRemovingAttempt, setIsRemovingAttempt] = useState(false);

  // Optimistic updates state
  const [optimisticAttempts, setOptimisticAttempts] = useState<{
    [key: string]: Attempt[];
  }>({});
  const [optimisticScore, setOptimisticScore] = useState<any>(null);

  // Auto-hide message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    const loadAthletes = async () => {
      const allAthletes = await store.getAthletes();
      setAthletes(allAthletes);
      setFilteredAthletes(allAthletes);
    };
    loadAthletes();
  }, [store]);

  useEffect(() => {
    if (!selectedBatch) {
      setFilteredAthletes([]);
      return;
    }

    let filtered = athletes.filter((athlete) => athlete.timeSlot === selectedBatch);

    if (athleteSearch.trim()) {
      filtered = filtered.filter((athlete) =>
        athlete.name.toLowerCase().includes(athleteSearch.toLowerCase()),
      );
    }

    setFilteredAthletes(
      filtered.sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );
  }, [athleteSearch, athletes, selectedBatch]);

  // Load route status and athlete score when selected athlete changes or refresh is triggered
  useEffect(() => {
    if (!selectedAthlete) {
      setAthleteScore(null);
      setOptimisticAttempts({});
      setOptimisticScore(null);
      setIsUpdatingData(false);
      return;
    }

    // Clear previous data before loading new athlete
    setAthleteScore(null);
    setOptimisticAttempts({});
    setOptimisticScore(null);
    setIsUpdatingData(true);

    const loadAthleteData = async () => {
      try {
        // Load all attempts for this athlete first to populate optimistic state
        console.log(
          `🔍 Loading attempts for athlete: ${selectedAthlete.name} (${selectedAthlete.id})`,
        );
        const allAttempts = await store.getAthleteAttempts(selectedAthlete.id);
        console.log(`📊 Found ${allAttempts.length} attempts:`, allAttempts);

        // Group attempts by route for optimistic state
        const attemptsMap: { [key: string]: Attempt[] } = {};
        allAttempts.forEach((attempt) => {
          const key = `${selectedAthlete.id}-${attempt.routeNumber}`;
          if (!attemptsMap[key]) {
            attemptsMap[key] = [];
          }
          attemptsMap[key].push(attempt);
        });
        setOptimisticAttempts(attemptsMap);
        console.log(`🗂️ Attempts map:`, attemptsMap);

        // Calculate optimistic score using the fresh data directly
        const optimisticScoreData = calculateOptimisticScore(
          selectedAthlete.id,
          attemptsMap, // Pass the fresh data directly
        );
        console.log(`🎯 Optimistic score calculated:`, optimisticScoreData);
        setOptimisticScore(optimisticScoreData);

        // Load athlete score
        const score = await store.getAthleteScore(selectedAthlete.id);
        console.log(`📈 Athlete score from store:`, score);
        setAthleteScore(score);
      } finally {
        setIsUpdatingData(false);
      }
    };

    loadAthleteData();
  }, [selectedAthlete, refreshTrigger, store]);


  const handleLogin = async () => {
    const judge = await store.validateJudge(password);
    if (judge) {
      setCurrentJudge(judge);
      setIsAuthenticated(true);
      setPassword("");
      setMessage("");
    } else {
      setMessage("Senha incorreta. Tente novamente.");
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentJudge(null);
    setSelectedBatch(null);
    setSelectedAthlete(null);
    setSelectedRoute(null);
    setMessage("");
  };

  const recordAttempt = async (completed: boolean) => {
    if (!selectedAthlete || !selectedRoute || isRecording) {
      if (!selectedAthlete || !selectedRoute) {
        setMessage("Atleta ou via não selecionados.");
      }
      return;
    }

    setIsRecording(true);

    console.log(
      `🎯 Recording attempt for ${selectedAthlete.name}, Route ${selectedRoute}, Completed: ${completed}`,
    );
    console.log(
      `📊 Using store: ${store === supabaseStore ? "Supabase" : "localStorage"}`,
    );

    try {
      // Get existing attempts for validation
      const key = `${selectedAthlete.id}-${selectedRoute}`;
      const existingAttempts = getOptimisticRouteAttempts(
        selectedAthlete.id,
        selectedRoute,
      );
      const hasCompleted = existingAttempts.some(
        (a) => a.result === "completed",
      );

      if (hasCompleted && completed) {
        setMessage(
          "Este atleta já completou esta via. Use a função de edição se necessário.",
        );
        return;
      }

      // Calculate attempt details locally
      const attemptNumber = existingAttempts.length + 1;
      let points = 0;
      let pointType: "flash" | "second+" | "none" = "none";

      if (completed && !hasCompleted) {
        const route = ROUTES.find((r) => r.number === selectedRoute)!;
        if (attemptNumber === 1) {
          points = route.flashPoints;
          pointType = "flash";
        } else {
          points = route.secondPlusPoints;
          pointType = "second+";
        }
      }

      // Create optimistic attempt
      const optimisticAttempt: Attempt = {
        id: `temp-${Date.now()}`,
        athleteId: selectedAthlete.id,
        routeNumber: selectedRoute,
        timestamp: new Date().toISOString(),
        result: completed ? "completed" : "failed",
        attemptNumber,
        points,
        pointType,
      };

      // Update optimistic state immediately
      setOptimisticAttempts((prev) => ({
        ...prev,
        [key]: [...existingAttempts, optimisticAttempt],
      }));

      // Update optimistic score with the new attempts
      const updatedAttempts = {
        ...optimisticAttempts,
        [key]: [...existingAttempts, optimisticAttempt],
      };
      const newScore = calculateOptimisticScore(
        selectedAthlete.id,
        updatedAttempts,
      );
      setOptimisticScore(newScore);

      const resultText = completed
        ? pointType === "flash"
          ? `Via ${selectedRoute} - Flash (+${points} pontos)`
          : `Via ${selectedRoute} - 2ª+ (+${points} pontos)`
        : `Via ${selectedRoute} - Falha registrada!`;

      setMessage(completed ? `✅ ${resultText} - Registrado!` : `❌ ${resultText}`);

      // Save to database in background (don't await)
      store
        .addAttempt(selectedAthlete.id, selectedRoute, completed)
        .then((savedAttempt) => {
          console.log(`✅ Attempt saved to database:`, savedAttempt);
          // Optionally sync back from database to ensure consistency
          // but don't change UI since optimistic update already happened
        })
        .catch((error) => {
          console.error("❌ Error saving to database:", error);
          // Rollback optimistic changes
          setOptimisticAttempts((prev) => {
            const updated = { ...prev };
            const attempts = updated[key] || [];
            updated[key] = attempts.filter(
              (a) => a.id !== optimisticAttempt.id,
            );

            // Recalculate score after rollback
            const rolledBackScore = calculateOptimisticScore(
              selectedAthlete.id,
              updated,
            );
            setOptimisticScore(rolledBackScore);

            return updated;
          });

          // Show error message
          setMessage("❌ Erro ao salvar no banco. A tentativa foi removida.");
        });
    } catch (error) {
      console.error("❌ Error in optimistic update:", error);
      setMessage("Erro ao registrar tentativa. Tente novamente.");
    } finally {
      setIsRecording(false);
    }
  };

  const cancelLastAttempt = async () => {
    if (!selectedAthlete || !selectedRoute || isRemovingAttempt) {
      return;
    }

    setIsRemovingAttempt(true);
    setMessage("Cancelando última tentativa...");

    try {
      const success = await store.removeLastAttempt(
        selectedAthlete.id,
        selectedRoute,
      );

      if (success) {
        setMessage(
          `✅ Última tentativa da via ${selectedRoute} cancelada com sucesso!`,
        );

        // Update optimistic state immediately
        const key = `${selectedAthlete.id}-${selectedRoute}`;
        setOptimisticAttempts((prev) => {
          const attempts = prev[key] || [];
          if (attempts.length === 0) return prev;

          // Remove the last attempt
          const updatedAttempts = [...attempts];
          updatedAttempts.pop();

          return {
            ...prev,
            [key]: updatedAttempts,
          };
        });

        // Update optimistic score
        const newScore = calculateOptimisticScore(selectedAthlete.id);
        setOptimisticScore(newScore);

        // Force re-render
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setMessage(
          "❌ Falha ao cancelar tentativa. Verifique se a política de DELETE foi configurada no banco de dados.",
        );
      }
    } catch (error) {
      console.error("❌ Error cancelling attempt:", error);
      setMessage("❌ Erro ao cancelar tentativa.");
    } finally {
      setIsRemovingAttempt(false);
    }
  };

  const cancelAllRouteAttempts = async () => {
    if (!selectedAthlete || !selectedRoute || isRemovingAttempt) {
      return;
    }

    setIsRemovingAttempt(true);
    setMessage("Cancelando todas as tentativas da via...");

    try {
      const removedCount = await store.removeAllRouteAttempts(
        selectedAthlete.id,
        selectedRoute,
      );

      if (removedCount > 0) {
        setMessage(
          `✅ ${removedCount} tentativa(s) da via ${selectedRoute} cancelada(s) com sucesso!`,
        );

        // Update optimistic state immediately
        const key = `${selectedAthlete.id}-${selectedRoute}`;
        setOptimisticAttempts((prev) => ({
          ...prev,
          [key]: [],
        }));

        // Update optimistic score
        const newScore = calculateOptimisticScore(selectedAthlete.id);
        setOptimisticScore(newScore);

        // Force re-render
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setMessage(
          "❌ Falha ao cancelar tentativas. Verifique se a política de DELETE foi configurada no banco de dados.",
        );
      }
    } catch (error) {
      console.error("❌ Error cancelling attempts:", error);
      setMessage("❌ Erro ao cancelar tentativas.");
    } finally {
      setIsRemovingAttempt(false);
    }
  };

  // Local calculation utilities for optimistic updates
  const getOptimisticRouteAttempts = (
    athleteId: string,
    routeNumber: number,
  ): Attempt[] => {
    const key = `${athleteId}-${routeNumber}`;
    return optimisticAttempts[key] || [];
  };

  const calculateOptimisticRouteStatus = (
    athleteId: string,
    routeNumber: number,
  ) => {
    const attempts = getOptimisticRouteAttempts(athleteId, routeNumber);
    const completed = attempts.find((a) => a.result === "completed");

    if (completed) {
      return {
        status: completed.pointType as "flash" | "second+",
        points: completed.points,
        attempts: attempts.length,
      };
    }

    return {
      status: attempts.length > 0 ? "failed" : "not-attempted",
      points: 0,
      attempts: attempts.length,
    };
  };


  const calculateOptimisticScore = (
    athleteId: string,
    currentOptimisticAttempts?: { [key: string]: Attempt[] },
  ) => {
    console.log(`🧮 Calculating optimistic score for athlete: ${athleteId}`);
    const attemptsToUse = currentOptimisticAttempts || optimisticAttempts;
    console.log(`📝 Using attempts data:`, attemptsToUse);
    const allAttempts: Attempt[] = [];

    // Collect all attempts for this athlete
    Object.entries(attemptsToUse).forEach(([key, attempts]) => {
      if (key.startsWith(athleteId)) {
        console.log(`🔑 Found key ${key} with ${attempts.length} attempts`);
        allAttempts.push(...attempts);
      }
    });
    console.log(`🎯 Total attempts collected:`, allAttempts);

    // Group by route and get best score for each route
    const routeScores = new Map<
      number,
      { points: number; routeNumber: number }
    >();

    allAttempts.forEach((attempt) => {
      if (attempt.result === "completed") {
        const existing = routeScores.get(attempt.routeNumber);
        if (!existing || attempt.points > existing.points) {
          routeScores.set(attempt.routeNumber, {
            points: attempt.points,
            routeNumber: attempt.routeNumber,
          });
        }
      }
    });

    // Get top 6 routes
    const allScores = Array.from(routeScores.values())
      .sort((a, b) => b.points - a.points)
      .slice(0, 6);

    const totalScore = allScores.reduce((sum, score) => sum + score.points, 0);

    const result = {
      totalScore,
      topSixRoutes: allScores,
    };

    console.log(`🏅 Optimistic score result:`, result);

    return result;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white overflow-x-hidden">
        <header className="bg-primary text-white py-4">
          <div className="container mx-auto px-4">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
              </Link>
              <h1 className="text-2xl font-bold">Área do Juiz</h1>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader className="text-center">
                <CardTitle>Autenticação do Juiz</CardTitle>
                <CardDescription>
                  Digite sua senha para acessar a área de registro
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="password">Senha</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleLogin()}
                      placeholder="Digite sua senha"
                    />
                  </div>
                  <Button onClick={handleLogin} className="w-full">
                    Entrar
                  </Button>
                  {message && (
                    <Alert>
                      <AlertDescription>{message}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <header className="bg-primary text-white py-2 sm:py-4">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 text-xs sm:text-sm"
                >
                  <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Voltar</span>
                </Button>
              </Link>
              <h1 className="text-xl sm:text-2xl font-bold">Área do Juiz</h1>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm hidden sm:inline">
                Logado como: {currentJudge?.name}
              </span>
              <span className="text-lg sm:hidden">{currentJudge?.name}</span>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 text-xs sm:text-sm"
              >
                <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Fixed Header with Selected Athlete - Mobile Only */}
      {selectedAthlete && (
        <div className="fixed top-0 left-0 right-0 bg-primary text-white p-4 z-40 flex justify-between items-center shadow-lg sm:hidden">
          <h2 className="text-2xl font-bold truncate flex-1">
            <div className="text-3xl font-black">{selectedAthlete.name}</div>
            {selectedRoute && <div className="text-lg text-primary-foreground/90 mt-1">Via {selectedRoute}</div>}
          </h2>
          <button
            onClick={() => {
              setSelectedAthlete(null);
              setSelectedRoute(null);
            }}
            className="text-3xl hover:opacity-75 transition-opacity flex-shrink-0 ml-3"
            aria-label="Clear athlete"
          >
            ×
          </button>
        </div>
      )}

      <main className={`w-full sm:container sm:mx-auto px-0 sm:px-4 py-4 sm:py-8 ${selectedAthlete ? "sm:pt-0" : ""}`} style={selectedAthlete ? { paddingTop: "50px", paddingBottom: selectedRoute ? "1rem" : "1rem" } : {}}>
        {/* Sticky Header with Selected Athlete - Desktop */}
        {selectedAthlete && (
          <div className="hidden sm:flex bg-primary text-white p-4 rounded-lg mb-4 z-20 justify-between items-center shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold">
              {selectedRoute && <span>Via {selectedRoute} - </span>}
              {selectedAthlete.name}
            </h2>
            <button
              onClick={() => {
                setSelectedAthlete(null);
                setSelectedRoute(null);
              }}
              className="text-2xl hover:opacity-75 transition-opacity"
              aria-label="Clear athlete"
            >
              ×
            </button>
          </div>
        )}
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-0 sm:gap-4 lg:gap-8">
          {/* Selection Panel - Hidden when athlete selected */}
          {!selectedAthlete && (
          <Card className="lg:col-span-1 order-1 rounded-none sm:rounded-lg">
            <CardHeader>
              <CardTitle className="text-2xl sm:text-xl font-bold">Seleção</CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Selecione o atleta e a via para registro
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-xl sm:text-base font-medium">Bateria</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBatch("11:00");
                        setSelectedAthlete(null);
                        setAthleteSearch("");
                      }}
                      className={`p-3 sm:p-2 rounded border-2 text-lg sm:text-sm font-medium transition-colors ${
                        selectedBatch === "11:00"
                          ? "bg-primary text-white border-primary"
                          : "bg-white border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      11:00
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBatch("14:30");
                        setSelectedAthlete(null);
                        setAthleteSearch("");
                      }}
                      className={`p-3 sm:p-2 rounded border-2 text-lg sm:text-sm font-medium transition-colors ${
                        selectedBatch === "14:30"
                          ? "bg-primary text-white border-primary"
                          : "bg-white border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      14:30
                    </button>
                  </div>
                </div>

                {selectedBatch && (
                  <div>
                    <Label className="text-xl sm:text-base font-medium">Atleta</Label>
                    <div className="space-y-2">
                      <Input
                        placeholder="Buscar atleta por nome..."
                        value={athleteSearch}
                        onChange={(e) => setAthleteSearch(e.target.value)}
                        className="h-14 sm:h-10 text-xl sm:text-sm"
                      />
                      <div className="max-h-96 sm:max-h-[calc(100vh-400px)] overflow-y-auto border rounded-md">
                        {filteredAthletes.length > 0 ? (
                          <div className="space-y-1 p-1">
                            {filteredAthletes.map((athlete) => (
                              <button
                                key={athlete.id}
                                type="button"
                                className={`w-full text-left p-4 sm:p-2 rounded text-base sm:text-sm hover:bg-gray-100 transition-colors ${
                                  selectedAthlete?.id === athlete.id
                                    ? "bg-primary text-white hover:bg-primary/90"
                                    : "bg-white"
                                }`}
                                onClick={() => {
                                  setSelectedAthlete(athlete);
                                  setAthleteSearch("");
                                }}
                              >
                                <div className="font-medium text-lg sm:text-base">{athlete.name}</div>
                                <div className="text-base sm:text-xs opacity-70 mt-0.5">
                                  {athlete.gender} | Bateria {athlete.timeSlot}
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-base sm:text-sm">
                            {athleteSearch
                              ? "Nenhum atleta encontrado"
                              : "Selecione uma bateria"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          )}

          {/* Loading State */}
          {selectedAthlete && isUpdatingData && (
            <Card className={`${!selectedAthlete ? "lg:col-span-2" : "lg:col-span-3"} order-2 lg:order-2 rounded-none sm:rounded-lg`}>
              <CardHeader>
                <CardTitle className="text-2xl sm:text-xl">Carregando dados: {selectedAthlete.name}</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  {selectedAthlete.gender} | Bateria{" "}
                  {selectedAthlete.timeSlot}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-8 space-y-4">
                  <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="text-gray-600 text-base sm:text-sm">
                    Carregando pontuação e histórico de vias...
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Athlete Progress */}
          {selectedAthlete && !isUpdatingData && (
            <Card className={`${!selectedAthlete ? "lg:col-span-2" : "lg:col-span-3"} order-2 lg:order-2 rounded-none sm:rounded-lg`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-2xl sm:text-xl">Progresso: {selectedAthlete.name}</CardTitle>
                    <CardDescription className="text-sm sm:text-base mt-1">
                      {selectedAthlete.gender} | Bateria{" "}
                      {selectedAthlete.timeSlot}
                    </CardDescription>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedAthlete(null);
                      setSelectedRoute(null);
                      setAthleteSearch("");
                    }}
                    className="text-2xl hover:opacity-75 transition-opacity flex-shrink-0 ml-3"
                    aria-label="Clear athlete"
                  >
                    ×
                  </button>
                </div>
              </CardHeader>
              <CardContent className="pb-0 sm:pb-[180px]">
                <div className="space-y-4">
                  {(() => {
                    // Always use optimistic score if available (real-time updates), fallback to athleteScore
                    const score = optimisticScore ||
                      athleteScore || { totalScore: 0, topSixRoutes: [] };
                    const hasOptimisticData =
                      optimisticScore !== null;
                    return (
                      <>
                        <div className="bg-primary/10 p-4 rounded">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-xl sm:text-sm">
                              Pontuação Total:
                            </span>
                            {isUpdatingData && !hasOptimisticData ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-5 h-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                <span className="text-sm sm:text-xs text-gray-500">
                                  Atualizando...
                                </span>
                              </div>
                            ) : (
                              <span className="text-3xl sm:text-2xl font-bold text-primary">
                                {score.totalScore}
                              </span>
                            )}
                          </div>
                          <div className="text-base sm:text-sm text-gray-600 mt-1">
                            {isUpdatingData && !hasOptimisticData ? (
                              <div className="h-4 bg-gray-200 rounded animate-pulse" />
                            ) : (
                              <>
                                Soma das 6 melhores vias:{" "}
                                {score.topSixRoutes
                                  .map((r) => r.routeNumber)
                                  .join(", ")}
                              </>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-10 gap-2 sm:gap-3">
                          {Array.from({ length: 40 }, (_, i) => {
                            const routeNumber = i + 1;
                            const status = calculateOptimisticRouteStatus(
                              selectedAthlete.id,
                              routeNumber,
                            );
                            const isTopSix = score.topSixRoutes.some(
                              (r) => r.routeNumber === routeNumber,
                            );
                            const hasOptimisticData =
                              optimisticAttempts[
                                `${selectedAthlete.id}-${routeNumber}`
                              ] !== undefined;

                            return (
                              <Button
                                key={routeNumber}
                                variant={
                                  selectedRoute === routeNumber
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                disabled={isUpdatingData && !hasOptimisticData}
                                className={`h-16 sm:h-20 text-sm sm:text-base font-medium ${
                                  isUpdatingData && !hasOptimisticData
                                    ? "opacity-50 cursor-not-allowed"
                                    : status.status === "flash"
                                      ? "bg-green-100 border-green-300 text-green-900"
                                      : status.status === "second+"
                                        ? "bg-blue-100 border-blue-300 text-blue-900"
                                        : status.status === "failed"
                                          ? "bg-red-200 border-red-500 text-red-900"
                                          : ""
                                } ${isTopSix ? "ring-2 ring-yellow-400" : ""}`}
                                onClick={() => setSelectedRoute(routeNumber)}
                              >
                                <div className="text-center">
                                  <div className="font-bold text-3xl sm:text-lg">
                                    {routeNumber}
                                  </div>
                                  {isUpdatingData && !hasOptimisticData ? (
                                    <div className="w-4 h-4 mx-auto mt-1 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                  ) : status.attempts > 0 ? (
                                    <div className="text-sm sm:text-base mt-1">
                                      {status.status === "flash"
                                        ? "F"
                                        : status.status === "second+"
                                          ? "2+"
                                          : status.attempts}
                                    </div>
                                  ) : null}
                                </div>
                              </Button>
                            );
                          })}
                        </div>

                        <div className="text-xl sm:text-xs text-gray-600 text-center">
                          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                            <span>🟢 Flash</span>
                            <span>🔵 2ª+</span>
                            <span>🔴 Falhou</span>
                            <span>🟡 Top 6</span>
                          </div>
                        </div>

                        {selectedAthlete && selectedRoute && (
                          <div className="bg-gray-50 p-4 sm:p-4 rounded mt-4">
                            <h4 className="font-medium text-base sm:text-sm mb-3">
                              Status da Via {selectedRoute} -{" "}
                              {selectedAthlete.name}
                            </h4>
                            <div className="space-y-2 text-base sm:text-sm">
                              {(() => {
                                // Calculate status directly from optimistic data
                                const status = calculateOptimisticRouteStatus(
                                  selectedAthlete.id,
                                  selectedRoute,
                                );

                                // Check if we're still loading data (no optimistic data yet)
                                const isLoading =
                                  isUpdatingData &&
                                  !optimisticAttempts[
                                    `${selectedAthlete.id}-${selectedRoute}`
                                  ];

                                return (
                                  <>
                                    <div className="flex justify-between items-center">
                                      <span className="font-medium">Status:</span>
                                      {isLoading ? (
                                        <div className="flex items-center space-x-2">
                                          <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                                          <span className="text-sm sm:text-xs text-gray-500">
                                            Atualizando...
                                          </span>
                                        </div>
                                      ) : (
                                        <Badge
                                          variant={
                                            status.status === "flash"
                                              ? "default"
                                              : status.status === "second+"
                                                ? "secondary"
                                                : status.status === "failed"
                                                  ? "destructive"
                                                  : "outline"
                                          }
                                          className="text-sm sm:text-xs"
                                        >
                                          {status.status === "flash"
                                            ? "Flash"
                                            : status.status === "second+"
                                              ? "2ª+"
                                              : status.status === "failed"
                                                ? "Falhou"
                                                : "Não tentou"}
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Tentativas:</span>
                                      {isLoading ? (
                                        <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
                                      ) : (
                                        <span>{status.attempts}</span>
                                      )}
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Pontos:</span>
                                      {isLoading ? (
                                        <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
                                      ) : (
                                        <span>{status.points}</span>
                                      )}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Cancel Attempts Section */}
                        {selectedAthlete && selectedRoute && (
                          <div className="pt-4 border-t border-gray-200" style={{ paddingBottom: "200px" }}>
                            <p className="text-xl sm:text-sm text-gray-600 mb-0 font-medium">
                              Cancelar tentativas registradas incorretamente:
                            </p>
                            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isRecording || isRemovingAttempt}
                                    className="flex-1 text-base sm:text-sm py-2 sm:py-2 h-10 sm:h-9"
                                  >
                                    {isRemovingAttempt ? (
                                      <>
                                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        Cancelando...
                                      </>
                                    ) : (
                                      <>
                                        <Undo2 className="w-4 h-4 mr-2 sm:w-3 sm:h-3" />
                                        Última Tentativa
                                      </>
                                    )}
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Cancelar Última Tentativa
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Tem certeza que deseja cancelar a última
                                      tentativa do atleta{" "}
                                      <strong>{selectedAthlete.name}</strong>{" "}
                                      na <strong>Via {selectedRoute}</strong>?
                                      <br />
                                      <br />
                                      Esta ação não pode ser desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancelar
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={cancelLastAttempt}
                                    >
                                      Sim, Cancelar Tentativa
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>

                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isRecording || isRemovingAttempt}
                                    className="flex-1 text-base sm:text-sm py-2 sm:py-2 h-10 sm:h-9"
                                  >
                                    <RotateCcw className="w-4 h-4 mr-2 sm:w-3 sm:h-3" />
                                    Todas da Via
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Cancelar TODAS as Tentativas
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      <strong>⚠️ ATENÇÃO:</strong> Tem certeza
                                      que deseja cancelar <strong>TODAS</strong>{" "}
                                      as tentativas do atleta{" "}
                                      <strong>{selectedAthlete.name}</strong>{" "}
                                      na <strong>Via {selectedRoute}</strong>?
                                      <br />
                                      <br />
                                      Esta ação removerá completamente o
                                      histórico desta via e não pode ser
                                      desfeita.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>
                                      Cancelar
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={cancelAllRouteAttempts}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Sim, Remover TODAS
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        )}

                        {/* Action Buttons - Desktop Only */}
                        {selectedAthlete && selectedRoute && (
                          <div className="hidden sm:flex mt-4 gap-3 sm:gap-4">
                            <Button
                              onClick={() => recordAttempt(true)}
                              disabled={
                                !selectedAthlete ||
                                !selectedRoute ||
                                isRecording
                              }
                              className="flex-1 h-12 text-base sm:text-sm font-bold justify-center"
                              size="lg"
                            >
                              {isRecording ? (
                                <>
                                  <div className="w-5 h-5 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                  Processando...
                                </>
                              ) : (
                                <>
                                  <Check className="w-5 h-5 mr-2" />
                                  Completou
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => recordAttempt(false)}
                              disabled={
                                !selectedAthlete ||
                                !selectedRoute ||
                                isRecording
                              }
                              variant="destructive"
                              className="flex-1 h-12 text-base sm:text-sm font-bold justify-center"
                              size="lg"
                            >
                              {isRecording ? (
                                <>
                                  <div className="w-5 h-5 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                  Processando...
                                </>
                              ) : (
                                <>
                                  <X className="w-5 h-5 mr-2" />
                                  Falhou
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Floating Toast Message */}
      {message && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] animate-in fade-in duration-300">
          {message.includes("Falha") ? (
            <Alert className="bg-red-50 border-red-300 text-red-800 shadow-lg">
              <AlertDescription className="text-lg sm:text-base font-medium">
                {message.replace("✅", "❌")}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-green-50 border-green-300 text-green-800 shadow-lg">
              <AlertDescription className="text-lg sm:text-base font-medium">
                {message}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Floating Action Buttons - Mobile Only */}
      {selectedAthlete && selectedRoute && (
        <div className="fixed bottom-0 left-0 right-0 sm:hidden bg-gray-200 border-t border-gray-200 shadow-lg p-3 flex flex-col gap-3 z-50 pb-5">
          <div className="flex gap-3">
            <Button
              onClick={() => recordAttempt(true)}
              disabled={
                !selectedAthlete ||
                !selectedRoute ||
                isRecording
              }
              className="flex-1 h-14 text-base font-bold justify-center"
              size="lg"
            >
              {isRecording ? (
                <>
                  <div className="w-5 h-5 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Processando...
                </>
              ) : (
                <>
                  <Check className="w-6 h-6 mr-2" />
                  Completou
                </>
              )}
            </Button>
            <Button
              onClick={() => recordAttempt(false)}
              disabled={
                !selectedAthlete ||
                !selectedRoute ||
                isRecording
              }
              variant="destructive"
              className="flex-1 h-14 text-base font-bold justify-center"
              size="lg"
            >
              {isRecording ? (
                <>
                  <div className="w-5 h-5 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Processando...
                </>
              ) : (
                <>
                  <X className="w-6 h-6 mr-2" />
                  Falhou
                </>
              )}
            </Button>
          </div>

          <Button
            onClick={() => {
              setSelectedAthlete(null);
              setSelectedRoute(null);
              setAthleteSearch("");
            }}
            variant="outline"
            className="w-full h-12 text-base font-bold justify-center"
            size="lg"
          >
            Próximo Atleta
          </Button>
        </div>
      )}

      {/* Floating "Próximo Atleta" Button - Desktop */}
      {selectedAthlete && !selectedRoute && (
        <div className="hidden sm:block fixed bottom-4 right-4 z-50">
          <Button
            onClick={() => {
              setSelectedAthlete(null);
              setSelectedRoute(null);
              setAthleteSearch("");
            }}
            variant="outline"
            className="h-12 px-6 text-base font-bold"
            size="lg"
          >
            Próximo Atleta
          </Button>
        </div>
      )}
    </div>
  );
}
